import React, { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { MessagesSquare, SendHorizonal, Loader2, CheckCircle2, Trash2, RotateCcw, UserRound } from "lucide-react";
import { cn } from "@/lib/utils";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const REACTIONS = [
  { kind: "up", emoji: "👍", label: "Thumbs up" },
  { kind: "heart", emoji: "❤️", label: "Love" },
  { kind: "party", emoji: "🎉", label: "Celebrate" },
];
const EXTRA_EMOJIS = ["🚀", "👀", "🙌", "🔥", "💡", "🤔", "⚡", "✅", "⚠️", "❓", "💯", "🙏", "👏"];

function ReactionPills({ comment, currentActor, onReact }) {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  if (!onReact) return null;
  const extraKinds = Object.keys(comment.reactions || {}).filter(
    (k) => !REACTIONS.some((r) => r.kind === k)
  );
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {REACTIONS.map((r) => {
        const bucket = comment.reactions?.[r.kind] || { count: 0, actors: [] };
        const iReacted = currentActor && (bucket.actors || []).includes(currentActor);
        return (
          <button key={r.kind} data-testid={`report-comment-react-${r.kind}-${comment.id}`}
            title={r.label} onClick={() => onReact(comment, r.kind)}
            className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border transition-colors",
              iReacted ? "bg-[color:rgba(43,78,255,0.12)] border-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)]"
                       : "border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:border-[color:var(--brand-ocean)]")}>
            <span className="text-[12px] leading-none">{r.emoji}</span>
            {bucket.count > 0 && <span className="tabular-nums" data-testid={`report-comment-react-count-${r.kind}-${comment.id}`}>{bucket.count}</span>}
          </button>
        );
      })}
      {extraKinds.map((kind) => {
        const bucket = comment.reactions[kind];
        if (!bucket || bucket.count === 0) return null;
        const iReacted = currentActor && (bucket.actors || []).includes(currentActor);
        return (
          <button key={kind} data-testid={`report-comment-react-${kind}-${comment.id}`}
            onClick={() => onReact(comment, kind)}
            className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border transition-colors",
              iReacted ? "bg-[color:rgba(43,78,255,0.12)] border-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)]"
                       : "border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:border-[color:var(--brand-ocean)]")}>
            <span className="text-[12px] leading-none">{kind}</span>
            <span className="tabular-nums" data-testid={`report-comment-react-count-${kind}-${comment.id}`}>{bucket.count}</span>
          </button>
        );
      })}
      <div className="relative">
        <button data-testid={`report-comment-react-more-${comment.id}`}
          onClick={() => setPickerOpen((v) => !v)} title="Add reaction"
          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] border border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:border-[color:var(--brand-ocean)] transition-colors">
          +
        </button>
        {pickerOpen && (
          <div data-testid={`report-comment-react-picker-${comment.id}`}
            className="absolute z-30 bottom-full mb-1 left-0 grid grid-cols-6 gap-1 p-2 rounded-xl bg-[color:var(--surface)] shadow-[0_8px_24px_rgba(0,0,0,0.16)] border border-[color:var(--glass-border)] min-w-[180px]">
            {EXTRA_EMOJIS.map((em) => (
              <button key={em}
                onClick={() => { onReact(comment, em); setPickerOpen(false); }}
                className="text-lg hover:bg-[color:var(--surface-2)] rounded p-0.5 leading-none">
                {em}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CommentThread({ items, canModerate, currentActor, onResolve, onDelete, onReact }) {
  return (
    <div className="space-y-2.5">
      {items.map((c) => (
        <div key={c.id} data-testid="report-comment-item" className={cn("glass-card p-3", c.resolved && "opacity-60")}>
          <div className="flex items-center gap-2">
            <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold",
              c.is_guest ? "bg-[color:rgba(120,120,120,0.15)] text-[color:var(--ink-2)]"
                : "bg-[color:rgba(43,78,255,0.12)] text-[color:var(--brand-ocean)]")}>
              {c.is_guest ? <UserRound className="w-3.5 h-3.5" /> : (c.user_name || "?").slice(0, 2).toUpperCase()}
            </div>
            <span className="text-xs font-semibold text-[color:var(--ink)] flex-1 truncate">
              {c.user_name || "Guest"}
              {c.is_guest && <span className="ml-1 text-[9px] font-normal uppercase tracking-wide text-[color:var(--ink-3)]">guest</span>}
            </span>
            <span className="text-[10px] text-[color:var(--ink-3)]">{new Date(c.created_at).toLocaleDateString()}</span>
          </div>
          <p className="text-sm text-[color:var(--ink-2)] mt-1.5 whitespace-pre-wrap break-words">{c.text}</p>
          <div className="flex items-center gap-1 mt-1.5">
            <ReactionPills comment={c} currentActor={currentActor} onReact={onReact} />
            {c.resolved && <Badge className="bg-[color:rgba(0,194,168,0.14)] text-[color:var(--success)] border-0 text-[10px]">Resolved</Badge>}
            <span className="flex-1" />
            {canModerate && (
              <>
                <button data-testid="report-comment-resolve" title={c.resolved ? "Reopen" : "Resolve"} onClick={() => onResolve(c)} className="text-[color:var(--ink-3)] hover:text-[color:var(--brand-mint)]">
                  {c.resolved ? <RotateCcw className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                </button>
                <button data-testid="report-comment-delete" title="Delete" onClick={() => onDelete(c)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] ml-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Designer (authenticated) panel: uses the api client + cookie session. */
export function ReportCommentsPanel({ open, onOpenChange, reportId, currentUserId, onCountChange }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    if (!reportId) return;
    api.get(`/report-designs/${reportId}/comments`)
      .then(({ data }) => { setItems(data); onCountChange?.(data.length); })
      .catch(() => {});
  }, [reportId, onCountChange]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/report-designs/${reportId}/comments`, { text: text.trim() }); setText(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to post"); }
    finally { setBusy(false); }
  };
  const resolve = async (c) => { try { await api.patch(`/report-designs/${reportId}/comments/${c.id}`, { resolved: !c.resolved }); load(); } catch { /* ignore */ } };
  const del = async (c) => { try { await api.delete(`/report-designs/${reportId}/comments/${c.id}`); load(); } catch { /* ignore */ } };
  const react = async (c, kind = "up") => {
    // Optimistic UI update, then confirm via API.
    setItems((prev) => prev.map((x) => {
      if (x.id !== c.id) return x;
      const actor = `u:${currentUserId || ""}`;
      const bucket = x.reactions?.[kind] || { count: 0, actors: [] };
      const has = bucket.actors.includes(actor);
      const nxt = has ? bucket.actors.filter((a) => a !== actor) : [...bucket.actors, actor];
      return { ...x, reactions: { ...(x.reactions || {}), [kind]: { count: nxt.length, actors: nxt } } };
    }));
    try { const { data } = await api.post(`/report-designs/${reportId}/comments/${c.id}/react`, { kind });
      setItems((prev) => prev.map((x) => (x.id === c.id ? data : x))); }
    catch { load(); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-sm p-0 flex flex-col bg-[color:var(--canvas)]" data-testid="report-comments-panel">
        <SheetHeader className="px-5 py-4 border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]">
          <SheetTitle className="flex items-center gap-2 font-display">
            <MessagesSquare className="w-5 h-5 text-[color:var(--brand-ocean)]" />Report comments
            <Badge variant="outline" className="ml-1 text-[color:var(--ink-3)]">{items.length}</Badge>
          </SheetTitle>
        </SheetHeader>
        <ScrollArea className="flex-1 px-4 py-3">
          {items.length === 0 && <p className="text-sm text-[color:var(--ink-3)] text-center py-8">No comments yet. Mention teammates with @their-email to notify them.</p>}
          <CommentThread items={items} canModerate currentActor={currentUserId ? `u:${currentUserId}` : ""} onResolve={resolve} onDelete={del} onReact={react} />
        </ScrollArea>
        <div className="p-3 border-t border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]">
          <Textarea data-testid="report-comment-input" rows={2} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment… use @email to mention" className="bg-[color:var(--surface)] text-sm" />
          <Button data-testid="report-comment-post" onClick={post} disabled={busy || !text.trim()} size="sm" className="mt-2 w-full bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <SendHorizonal className="w-4 h-4 mr-1" />}Post comment
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const GUEST_KEY = (t) => `eip_report_guest_${t}`;

/** Public (share-link) panel: uses axios directly + X-Share-Access header.
 *  When `versionIndex` is provided the panel scopes comments to that snapshot
 *  (approval flow) instead of the live draft. */
export function PublicReportCommentsPanel({ open, onOpenChange, token, accessToken, versionIndex }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [name, setName] = useState(() => (typeof window !== "undefined" && localStorage.getItem(GUEST_KEY(token))) || "");
  const [busy, setBusy] = useState(false);
  const headers = accessToken ? { "X-Share-Access": accessToken } : {};
  const base = `${API_BASE}/api/public/report/${token}/comments`;
  const vQuery = versionIndex != null ? `?version_index=${versionIndex}` : "";
  const load = useCallback(() => {
    if (!token) return;
    axios.get(`${base}${vQuery}`, { headers })
      .then(({ data }) => {
        // Latest view: exclude version-scoped notes so approval threads don't
        // leak into the live-draft conversation.
        const filtered = versionIndex == null
          ? (data || []).filter((c) => c.version_index == null)
          : (data || []);
        setItems(filtered);
      }).catch(() => setItems([]));
  }, [token, accessToken, versionIndex]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const guest = (name || "").trim() || "Guest";
      localStorage.setItem(GUEST_KEY(token), guest);
      const payload = { text: text.trim(), guest_name: guest };
      if (versionIndex != null) payload.version_index = versionIndex;
      await axios.post(base, payload, { headers });
      setText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to post"); }
    finally { setBusy(false); }
  };
  const react = async (c, kind = "up") => {
    const guest = ((name || "").trim() || "Guest").toLowerCase();
    const actor = `g:${guest}`;
    setItems((prev) => prev.map((x) => {
      if (x.id !== c.id) return x;
      const bucket = x.reactions?.[kind] || { count: 0, actors: [] };
      const has = bucket.actors.includes(actor);
      const nxt = has ? bucket.actors.filter((a) => a !== actor) : [...bucket.actors, actor];
      return { ...x, reactions: { ...(x.reactions || {}), [kind]: { count: nxt.length, actors: nxt } } };
    }));
    try {
      const { data } = await axios.post(`${base}/${c.id}/react`, { kind, guest_name: name || "Guest" }, { headers });
      setItems((prev) => prev.map((x) => (x.id === c.id ? data : x)));
    } catch { load(); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-sm p-0 flex flex-col bg-[color:var(--canvas)]" data-testid="public-report-comments-panel">
        <SheetHeader className="px-5 py-4 border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]">
          <SheetTitle className="flex items-center gap-2 font-display">
            <MessagesSquare className="w-5 h-5 text-[color:var(--brand-ocean)]" />Comments
            <Badge variant="outline" className="ml-1 text-[color:var(--ink-3)]">{items.length}</Badge>
            {versionIndex != null && (
              <Badge data-testid="public-comments-version-scope"
                className="bg-[color:var(--brand-mint)]/15 text-[color:var(--brand-ocean)] border-0">
                v{versionIndex + 1}
              </Badge>
            )}
          </SheetTitle>
          {versionIndex != null && (
            <p className="text-[11px] text-[color:var(--ink-3)]">
              Approval mode — comments and 👍 posted here are tied to this snapshot version only, not the live draft.
            </p>
          )}
        </SheetHeader>
        <ScrollArea className="flex-1 px-4 py-3">
          {items.length === 0 && <p className="text-sm text-[color:var(--ink-3)] text-center py-8">No comments yet — start the conversation below.</p>}
          <CommentThread items={items} canModerate={false}
            currentActor={`g:${((name || "").trim() || "Guest").toLowerCase()}`}
            onReact={react} />
        </ScrollArea>
        <div className="p-3 border-t border-[color:var(--glass-border)] bg-[color:var(--glass-bg)] space-y-2">
          <Input data-testid="public-comment-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Your name (visible to the team)" className="h-8 text-sm bg-[color:var(--surface)]" />
          <Textarea data-testid="public-comment-input" rows={2} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={versionIndex != null ? "Add approval note or feedback…" : "Add a comment…"}
            className="bg-[color:var(--surface)] text-sm" />
          <Button data-testid="public-comment-post" onClick={post} disabled={busy || !text.trim()} size="sm" className="w-full bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <SendHorizonal className="w-4 h-4 mr-1" />}
            {versionIndex != null ? "Post approval note" : "Post comment"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
